// src/components/pergola/Lamella.tsx
import React, { useMemo } from "react";
import { animated, useSpring } from "@react-spring/three";
import * as THREE from "three";
import { Euler, Shape } from "three";
import { Edges } from "@react-three/drei";
import type { Texture } from "three";
import { ConditionalAxesHelper } from "../Pergola";
import { ElementCoordinateSystem } from "../common_types";
import { computeOrientation } from "../transformations";

type Sezione = { larghezza: number; altezza: number };


export const Prototipo: React.FC<{
  sezione: Sezione;
  lunghezza: number;
  material: Texture;
  cs: ElementCoordinateSystem;
  rotazione?: number;
}> = ({ sezione, lunghezza, material, cs, rotazione = 0 }) => {
  const rotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);
  const rotazioneFinale: [number, number, number] = [rotation[0] + rotazione, rotation[1], rotation[2]];

  const shape = useMemo(() => {
    const s = new Shape();
    s.moveTo(-0.07069, 0.01935);
    s.lineTo(-0.09025, 0.01935);
    s.lineTo(-0.09025, 0.00828);
    s.lineTo(-0.09125, 0.00828);
    s.lineTo(-0.09125, 0.02280);
    s.lineTo(0.02356, 0.02280);
    s.lineTo(0.02356, 0);
    s.lineTo(0.09175, 0);
    s.lineTo(0.09175, 0.01452);
    s.lineTo(0.09325, 0.01452);
    s.lineTo(0.09325, 0);
    s.lineTo(0.09325, -0.02280);
    s.lineTo(0.09325, -0.02280);
    s.lineTo(-0.07069, -0.02280);
    return s;
  }, []);

  const { rotX, rotY, rotZ } = useSpring({
    rotX: rotazioneFinale[0],
    rotY: rotazioneFinale[1],
    rotZ: rotazioneFinale[2],
    config: { mass: 20, tension: 10, friction: 40 },
  });

  return (
    <animated.mesh
      position={[cs.org.x, cs.org.y, cs.org.z + sezione.altezza / 2]}
      rotation-x={rotX}
      rotation-y={rotY}
      rotation-z={rotZ}
    >
      <extrudeGeometry args={[shape, { depth: lunghezza, bevelEnabled: false }]} />
      <meshStandardMaterial
        map={material}
        map-wrapS={THREE.RepeatWrapping}
        map-wrapT={THREE.RepeatWrapping}
        map-repeat={[2, lunghezza]}
        map-needsUpdate={true}
      />
      <Edges color="#363636" />
    </animated.mesh>
  );
};

export const PrototipoProfondita: React.FC<{
  sezione: Sezione;
  lunghezza: number;
  material: Texture;
  cs: ElementCoordinateSystem;
  rotazione?: number;
}> = ({ sezione, lunghezza, material, cs, rotazione = 0 }) => {
  const rotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);
  const rotazioneFinale: [number, number, number] = [-Math.PI / 2, 0, rotation[2] + rotazione];

  const shape = useMemo(() => {
    const s = new Shape();
    s.moveTo(-0.07069, 0.01935);
    s.lineTo(-0.09025, 0.01935);
    s.lineTo(-0.09025, 0.00828);
    s.lineTo(-0.09125, 0.00828);
    s.lineTo(-0.09125, 0.02280);
    s.lineTo(0.02356, 0.02280);
    s.lineTo(0.02356, 0);
    s.lineTo(0.09175, 0);
    s.lineTo(0.09175, 0.01452);
    s.lineTo(0.09325, 0.01452);
    s.lineTo(0.09325, 0);
    s.lineTo(0.09325, -0.02280);
    s.lineTo(0.09325, -0.02280);
    s.lineTo(-0.07069, -0.02280);
    return s;
  }, []);

  const { rotX, rotY, rotZ } = useSpring({
    rotX: rotazioneFinale[0],
    rotY: rotazioneFinale[1],
    rotZ: rotazioneFinale[2],
    config: { mass: 20, tension: 10, friction: 40 },
  });

  return (
    <animated.mesh
      position={[cs.org.x, cs.org.y, cs.org.z + sezione.altezza / 2]}
      rotation-x={rotX}
      rotation-y={rotY}
      rotation-z={rotZ}
    >
      <extrudeGeometry args={[shape, { depth: lunghezza, bevelEnabled: false }]} />
      <meshStandardMaterial
        map={material}
        map-wrapS={THREE.RepeatWrapping}
        map-wrapT={THREE.RepeatWrapping}
        map-repeat={[2, lunghezza]}
        map-needsUpdate={true}
      />
      <Edges color="#363636" />
    </animated.mesh>
  );
};

export const ScartoDeLarghezza: React.FC<{
  sezione: Sezione;
  lunghezza: number;
  material: Texture;
  scarto: number;
  cs: ElementCoordinateSystem;
}> = (props) => {
  const rotation = useMemo(() => {
    const [rx, ry, rz] = computeOrientation(props.cs.direction);
    // Aggiungiamo 90° in senso orario sull'asse Z
    return new Euler(rx, ry, rz + Math.PI + Math.PI/2, 'XYZ');
  }, [props.cs.direction]);

  const shape = useMemo((): Shape => {
    const s = new Shape();
    s.moveTo(-0.01535, -0.0722);
    s.lineTo(-0.00835, -0.0722);
    s.lineTo(0.00665, -0.0722);
    s.lineTo(0.00665, -0.0698);
    s.lineTo(-0.01295, -0.0698);
    s.lineTo(-0.01295, -0.0358);
    s.lineTo(0.01535, -0.0358);
    s.lineTo(0.01535, -0.0334);
    s.lineTo(0.01535, 0.0722);
    s.lineTo(0.01295, 0.0722);
    s.lineTo(0.01295, -0.0334);
    s.lineTo(-0.01295, -0.0334);
    s.lineTo(-0.01295, 0.0698);
    s.lineTo(-0.01535, 0.0698);
    return s;
  }, []);

  return (
    <mesh
      position={[
        props.cs.org.x,
        props.cs.org.y,
        props.cs.org.z + props.sezione.altezza / 2,
      ]}
      rotation={rotation}
    >
      <extrudeGeometry
        args={[shape, { depth: props.lunghezza, bevelEnabled: false }]}
      />
      <meshStandardMaterial
        map={props.material}
        map-wrapS={THREE.RepeatWrapping}
        map-wrapT={THREE.RepeatWrapping}
        map-repeat={[2, props.lunghezza]}
        map-needsUpdate={true}
      />
      <ConditionalAxesHelper />
      <Edges color="#363636" />
    </mesh>
  );
};
export const ScartoDe: React.FC<{
  sezione: Sezione;
  lunghezza: number;
  material: Texture;
  scarto: number;
  cs: ElementCoordinateSystem;
}> = (props) => {
  const rotation = useMemo(
    () => computeOrientation(props.cs.direction),
    [props.cs.direction]
  );

  const shape = useMemo((): Shape => {
    const s = new Shape();
    s.moveTo(0.0722, -0.01535);
    s.lineTo(0.0722, -0.00835);
    s.lineTo(0.0722, 0.00665);
    s.lineTo(0.0698, 0.00665);
    s.lineTo(0.0698, -0.01295);
    s.lineTo(0.0358, -0.01295);
    s.lineTo(0.0358, 0.01535);
    s.lineTo(0.0334, 0.01535);
    s.lineTo(-0.0722, 0.01535);
    s.lineTo(-0.0722, 0.01295);
    s.lineTo(0.0334, 0.01295);
    s.lineTo(0.0334, -0.01295);
    s.lineTo(-0.0722, -0.01295);
    s.lineTo(-0.0722, -0.01535);
    return s;
  }, [props.scarto]);

  return (
    <mesh
      position={[
        props.cs.org.x,
        props.cs.org.y,
        props.cs.org.z + props.sezione.altezza / 2,
      ]}
      rotation={[rotation[0], rotation[1], rotation[2]]}
    >
      <extrudeGeometry
        args={[shape, { depth: props.lunghezza, bevelEnabled: false }]}
      />
      <meshStandardMaterial
        map={props.material}
        map-wrapS={THREE.RepeatWrapping}
        map-wrapT={THREE.RepeatWrapping}
        map-repeat={[2, props.lunghezza]}
        map-needsUpdate={true}
      />
      <ConditionalAxesHelper />
      <Edges color="#363636" />
    </mesh>
  );
};